import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Rocket, Info, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useNetwork, useAccount, useWalletClient } from 'wagmi';
import { getDexContracts, isDexSupported, getNativeTokenSymbol, getDexName, getDexVersion, V3_FEE_TIERS, supportsDualProtocol, isV2Supported, isV3Supported, getAvailableDexes, hasMultipleDexOptions, type DexOption } from '@/config/dex-v3-config';
import { UserTokenSelector } from '@/components/token/UserTokenSelector';
import { usePoolExists } from '@/hooks/usePoolExists';
import { useCreatePool } from '@/hooks/useCreatePool';
import { useAddLiquidity } from '@/hooks/useAddLiquidity';
import { useV2PoolExists } from '@/hooks/useV2PoolExists';
import { useAddV2Liquidity } from '@/hooks/useAddV2Liquidity';
import { isAddress } from 'viem';
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';
import { toast } from 'sonner';
import { ethers } from 'ethers';
import { getTxOptions } from '@/utils/gas';

export function LaunchPool() {
  const { chain } = useNetwork();
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { settings: feeSettings, loading: feeLoading } = useFirebaseFees();
  const [tokenAddress, setTokenAddress] = useState('');
  const [baseTokenAmount, setBaseTokenAmount] = useState('');
  const [quoteTokenAmount, setQuoteTokenAmount] = useState('');
  const [feeTier, setFeeTier] = useState(String(V3_FEE_TIERS.MEDIUM));
  const [isSupported, setIsSupported] = useState(false);
  const [nativeSymbol, setNativeSymbol] = useState('BNB');
  const [wethAddress, setWethAddress] = useState<string>('');
  const [dexName, setDexName] = useState<'PancakeSwap' | 'Uniswap' | 'DragonSwap' | 'TraderJoe' | null>(null);
  const [dexVersion, setDexVersion] = useState<'V2' | 'V3' | null>(null);
  const [selectedProtocol, setSelectedProtocol] = useState<'V2' | 'V3'>('V3');
  const [hasDualProtocol, setHasDualProtocol] = useState(false);
  const [hasV2, setHasV2] = useState(false);
  const [hasV3, setHasV3] = useState(false);
  const [availableDexes, setAvailableDexes] = useState<DexOption[]>([]);
  const [selectedDex, setSelectedDex] = useState<DexOption | null>(null);

  useEffect(() => {
    if (chain?.id) {
      setIsSupported(isDexSupported(chain.id));
      setNativeSymbol(getNativeTokenSymbol(chain.id));
      setDexName(getDexName(chain.id));
      setDexVersion(getDexVersion(chain.id));
      
      // Get available DEXes for this network
      const dexes = getAvailableDexes(chain.id);
      setAvailableDexes(dexes);
      
      // Set default selected DEX (first available, preferring V3)
      if (dexes.length > 0) {
        const defaultDex = dexes.find(d => d.version === 'V3') || dexes[0];
        setSelectedDex(defaultDex);
        setSelectedProtocol(defaultDex.version);
      }
      
      const dual = supportsDualProtocol(chain.id);
      const v2 = isV2Supported(chain.id);
      const v3 = isV3Supported(chain.id);
      
      setHasDualProtocol(dual);
      setHasV2(v2);
      setHasV3(v3);
      
      // Set default protocol based on what's available
      if (v3) {
        setSelectedProtocol('V3');
      } else if (v2) {
        setSelectedProtocol('V2');
      }
      
      const contracts = getDexContracts(chain.id);
      if (contracts) {
        setWethAddress(contracts.weth);
      }
    }
  }, [chain?.id]);

  // V3 Hooks
  const { exists: v3PoolExists } = usePoolExists(
    selectedProtocol === 'V3' && tokenAddress && isAddress(tokenAddress) ? tokenAddress : undefined,
    wethAddress,
    parseInt(feeTier)
  );

  const { createPool: createV3Pool, isCreating: isCreatingV3, canCreate: canCreateV3 } = useCreatePool(
    selectedProtocol === 'V3' && tokenAddress && isAddress(tokenAddress) ? tokenAddress : undefined,
    wethAddress,
    parseInt(feeTier)
  );

  const v3LiquidityParams = selectedProtocol === 'V3' && tokenAddress && baseTokenAmount && quoteTokenAmount && address && wethAddress
    ? {
        token0: tokenAddress,
        token1: wethAddress,
        feeTier: parseInt(feeTier),
        amount0: baseTokenAmount,
        amount1: quoteTokenAmount,
        recipient: address,
      }
    : null;

  const { addLiquidity: addV3Liquidity, isAdding: isAddingV3, canAdd: canAddV3 } = useAddLiquidity(v3LiquidityParams);

  // V2 Hooks
  const { exists: v2PoolExists } = useV2PoolExists(
    selectedProtocol === 'V2' && tokenAddress && isAddress(tokenAddress) ? tokenAddress : undefined,
    wethAddress
  );

  const v2LiquidityParams = selectedProtocol === 'V2' && tokenAddress && baseTokenAmount && quoteTokenAmount && address
    ? {
        token: tokenAddress,
        tokenAmount: baseTokenAmount,
        nativeAmount: quoteTokenAmount,
        recipient: address,
      }
    : null;

  const { addLiquidity: addV2Liquidity, isAdding: isAddingV2, canAdd: canAddV2 } = useAddV2Liquidity(v2LiquidityParams);

  // Unified state based on selected protocol
  const poolExists = selectedProtocol === 'V3' ? v3PoolExists : v2PoolExists;
  const isCreating = selectedProtocol === 'V3' ? isCreatingV3 : false; // V2 creates on first add
  const isAdding = selectedProtocol === 'V3' ? isAddingV3 : isAddingV2;

  // Helper function to get network fee
  const getNetworkFee = (feeType: 'addLiquidityFee') => {
    if (!feeSettings?.networkFees || !chain?.id) return '0';
    
    // Map testnet to mainnet for fee lookup
    const mainnetChainId = {
      97: 56,        // BSC Testnet -> BSC Mainnet
      5611: 204,     // opBNB Testnet -> opBNB Mainnet
      11155111: 1,   // Sepolia -> Ethereum Mainnet
      43113: 43114,  // Fuji Testnet -> Avalanche Mainnet
      11124: 2741,   // Abstract Sepolia -> Abstract Mainnet
      421614: 42161, // Arbitrum Sepolia -> Arbitrum One
      84532: 8453,   // Base Sepolia -> Base Mainnet
      11155420: 10,  // Optimism Sepolia -> Optimism Mainnet
      80002: 137,    // Polygon Amoy -> Polygon Mainnet
      1328: 1329     // Sei Testnet -> Sei Mainnet
    }[chain.id] || chain.id;

    // ETH-based networks that should use Ethereum fees if not found
    const ethBasedNetworks = [2741, 11124, 42161, 421614, 8453, 84532, 10, 11155420];
    
    let networkFees = feeSettings.networkFees[mainnetChainId];
    
    // If no fees found and it's an ETH-based network, use Ethereum fees
    if (!networkFees && ethBasedNetworks.includes(mainnetChainId)) {
      networkFees = feeSettings.networkFees[1]; // Ethereum mainnet
    }
    
    if (!networkFees) return '0';
    
    return networkFees[feeType] || '0';
  };

  const handleLaunchPool = async () => {
    try {
      // Check fee settings
      if (!feeSettings?.networkFees || feeLoading) {
        toast.error('Fee settings not loaded. Please try again.');
        return;
      }

      if (!walletClient) {
        toast.error('Wallet not connected');
        return;
      }

      // Calculate platform fee (percentage of native token amount)
      const feePercentage = parseFloat(getNetworkFee('addLiquidityFee'));
      if (isNaN(feePercentage) || feePercentage < 0) {
        toast.error('Invalid fee configuration');
        return;
      }

      // Calculate fee amount from the native token (quoteTokenAmount)
      const nativeAmount = parseFloat(quoteTokenAmount);
      if (isNaN(nativeAmount) || nativeAmount <= 0) {
        toast.error('Invalid native token amount');
        return;
      }

      const feeAmount = (nativeAmount * feePercentage) / 100;
      const feeCollectorAddress = feeSettings.collectors[0].address;

      // Send platform fee if fee > 0
      if (feeAmount > 0) {
        toast.info(`Collecting ${feePercentage}% platform fee (${feeAmount.toFixed(6)} ${nativeSymbol})...`);
        
        const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
        const signer = provider.getSigner();
        const platformFeeAmount = ethers.utils.parseEther(feeAmount.toString());

        console.log('Sending platform fee transaction...');
        console.log('Fee collector:', feeCollectorAddress);
        console.log('Fee amount:', platformFeeAmount.toString());

        const feeTx = await signer.sendTransaction({
          to: feeCollectorAddress,
          value: platformFeeAmount,
          ...getTxOptions(50000)
        });

        toast.info('Processing platform fee payment...');
        await feeTx.wait();
        toast.success('Platform fee paid successfully!');
      }

      // Now proceed with pool creation/liquidity addition
      if (selectedProtocol === 'V3') {
        // V3 Logic
        if (!v3PoolExists && canCreateV3) {
          // First create the V3 pool
          createV3Pool();
        } else if (v3PoolExists && canAddV3) {
          // Pool exists, just add liquidity
          addV3Liquidity();
        }
      } else {
        // V2 Logic - V2 creates pair automatically on first liquidity add
        if (canAddV2) {
          addV2Liquidity();
        }
      }
    } catch (error: any) {
      console.error('Error in handleLaunchPool:', error);
      toast.error(error?.message || 'Failed to launch pool. Please try again.');
    }
  };

  const isValidToken = tokenAddress && isAddress(tokenAddress);
  const canLaunch = isConnected && isSupported && isValidToken && baseTokenAmount && quoteTokenAmount && !isCreating && !isAdding;

  return (
    <div className="space-y-6">
      {/* Network Support Alert */}
      {!isConnected ? (
        <Alert className="border-yellow-500/50 bg-yellow-500/10">
          <AlertCircle className="h-4 w-4 text-yellow-500" />
          <AlertDescription>
            Please connect your wallet to launch a liquidity pool.
          </AlertDescription>
        </Alert>
      ) : !isSupported ? (
        <Alert className="border-red-500/50 bg-red-500/10">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <AlertDescription>
            PancakeSwap V3 is not supported on {chain?.name || 'this network'}. Please switch to opBNB Mainnet, opBNB Testnet, or BSC.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertDescription>
            <strong>{chain?.name}</strong> is supported! You can launch {dexName} {dexVersion} pools on this network.
          </AlertDescription>
        </Alert>
      )}

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Launch a new {dexName} {dexVersion} liquidity pool for your token. You'll need to provide both your token and {nativeSymbol} to create the initial liquidity.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5" />
            Pool Configuration
          </CardTitle>
          <CardDescription>
            Enter the details for your new liquidity pool
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* DEX Selector (when multiple DEXes available) */}
          {availableDexes.length > 1 && (
            <div className="space-y-2">
              <Label>Select DEX</Label>
              <Select 
                value={selectedDex ? `${selectedDex.name}-${selectedDex.version}` : undefined}
                onValueChange={(value) => {
                  const [name, version] = value.split('-');
                  const dex = availableDexes.find(d => d.name === name && d.version === version);
                  if (dex) {
                    setSelectedDex(dex);
                    setSelectedProtocol(dex.version as 'V2' | 'V3');
                    setDexName(dex.name as any);
                    setDexVersion(dex.version);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a DEX">
                    {selectedDex && (
                      <div className="flex items-center gap-2">
                        <img 
                          src={selectedDex.logo} 
                          alt={selectedDex.name}
                          className="h-5 w-5 object-contain"
                        />
                        <span>{selectedDex.label}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {availableDexes.map((dex) => (
                    <SelectItem key={`${dex.name}-${dex.version}`} value={`${dex.name}-${dex.version}`}>
                      <div className="flex items-center gap-2">
                        <img 
                          src={dex.logo} 
                          alt={dex.name}
                          className="h-5 w-5 object-contain"
                        />
                        <span>{dex.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Multiple DEX options available on this network
              </p>
            </div>
          )}

          {/* Protocol Selector (V2/V3 Toggle) - Only show if single DEX with dual protocol */}
          {hasDualProtocol && availableDexes.length <= 1 && (
            <div className="space-y-2">
              <Label>Protocol Version</Label>
              <Tabs value={selectedProtocol} onValueChange={(value) => setSelectedProtocol(value as 'V2' | 'V3')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="V2" className="flex items-center gap-2">
                    🔵 V2 (Simple)
                  </TabsTrigger>
                  <TabsTrigger value="V3" className="flex items-center gap-2">
                    🟣 V3 (Advanced)
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="text-xs text-muted-foreground">
                {selectedProtocol === 'V2' 
                  ? 'V2: Full price range, simpler to use, LP tokens'
                  : 'V3: Custom price ranges, concentrated liquidity, NFT positions'}
              </p>
            </div>
          )}

          {/* User Token Selector */}
          <UserTokenSelector
            onSelectToken={(address) => setTokenAddress(address)}
            selectedToken={tokenAddress}
            label="Quick Select Your Token"
          />

          {/* Token Address */}
          <div className="space-y-2">
            <Label htmlFor="token-address">Token Address</Label>
            <Input
              id="token-address"
              placeholder="0x... or select from your tokens above"
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The address of the token you want to create a pool for
            </p>
          </div>

          {/* Token Amount */}
          <div className="space-y-2">
            <Label htmlFor="token-amount">Your Token Amount</Label>
            <Input
              id="token-amount"
              type="number"
              placeholder="0.0"
              value={baseTokenAmount}
              onChange={(e) => setBaseTokenAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Amount of your token to add to the pool
            </p>
          </div>

          {/* Base Token Amount */}
          <div className="space-y-2">
            <Label htmlFor="base-amount">{nativeSymbol} Amount</Label>
            <Input
              id="base-amount"
              type="number"
              placeholder="0.0"
              value={quoteTokenAmount}
              onChange={(e) => setQuoteTokenAmount(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Amount of {nativeSymbol} to pair with your token
            </p>
          </div>

          {/* Fee Tier Selection - Only for V3 */}
          {selectedProtocol === 'V3' && (
            <div className="space-y-2">
              <Label htmlFor="fee-tier">Fee Tier</Label>
              <Select value={feeTier} onValueChange={setFeeTier}>
                <SelectTrigger>
                  <SelectValue placeholder="Select fee tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(V3_FEE_TIERS.LOWEST)}>
                    0.01% - For stablecoin pairs
                  </SelectItem>
                  <SelectItem value={String(V3_FEE_TIERS.LOW)}>
                    0.05% - For correlated pairs
                  </SelectItem>
                  <SelectItem value={String(V3_FEE_TIERS.MEDIUM)}>
                    0.25% - For most pairs (recommended)
                  </SelectItem>
                  <SelectItem value={String(V3_FEE_TIERS.HIGH)}>
                    1% - For exotic/volatile pairs
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Fee tier determines the trading fee for your pool. Higher fees = more rewards but less trading volume.
              </p>
            </div>
          )}

          {/* V2 Info */}
          {selectedProtocol === 'V2' && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                V2 pools use a standard 0.3% fee and provide liquidity across the entire price range.
              </AlertDescription>
            </Alert>
          )}

          {/* Pool Status */}
          {isValidToken && wethAddress && (
            <Alert className={poolExists ? "bg-blue-500/10 border-blue-500/20" : "bg-green-500/10 border-green-500/20"}>
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">
                    {poolExists ? '✓ Pool Exists' : '⚠ Pool Does Not Exist'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {poolExists 
                      ? 'This pool already exists. You can add liquidity directly.'
                      : 'Pool will be created first, then liquidity will be added.'}
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Initial Price Display */}
          {baseTokenAmount && quoteTokenAmount && (
            <Alert className="bg-primary/10 border-primary/20">
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">Initial Price</p>
                  <p className="text-sm">
                    1 Token = {(parseFloat(quoteTokenAmount) / parseFloat(baseTokenAmount)).toFixed(6)} {nativeSymbol}
                  </p>
                  <p className="text-sm">
                    1 {nativeSymbol} = {(parseFloat(baseTokenAmount) / parseFloat(quoteTokenAmount)).toFixed(6)} Token
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Fee Tier: {(parseInt(feeTier) / 10000).toFixed(2)}%
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button 
              className="flex-1" 
              size="lg" 
              disabled={!canLaunch || isCreating || isAdding}
              onClick={handleLaunchPool}
            >
              {(isCreating || isAdding) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isCreating ? 'Creating Pool...' : 'Adding Liquidity...'}
                </>
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" />
                  {poolExists ? 'Add Liquidity' : 'Launch Pool'}
                </>
              )}
            </Button>
            <Button variant="outline" size="lg" onClick={() => {
              setTokenAddress('');
              setBaseTokenAmount('');
              setQuoteTokenAmount('');
            }}>
              Clear
            </Button>
          </div>

          {!isValidToken && tokenAddress && (
            <p className="text-xs text-destructive text-center">
              Invalid token address
            </p>
          )}

          {/* Platform Fee Display */}
          {!feeLoading && feeSettings?.networkFees && chain?.id && (
            <Alert className="border-primary/50 bg-primary/10 mt-4">
              <Info className="h-4 w-4 text-primary" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span>
                    <strong>Platform Fee:</strong> {getNetworkFee('addLiquidityFee')}% of {nativeSymbol} amount
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (Collected before adding liquidity)
                  </span>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
